import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { X, GripVertical, MoreVertical, ChevronDown, Trash2 } from "lucide-react";
import { useCanvasStore, type KFNodeData, type CanvasShape } from "@/store/useCanvasStore";
import { THEME } from "@/config/theme";

interface RevealItem {
  rfId: string;
  label: string;
  subLabel: string;
  step: number;
}

interface StepGroup {
  step: number;
  items: RevealItem[];
}

interface RevealEditBarProps {
  onClose: () => void;
  onDragHandleMouseDown?: (e: React.MouseEvent) => void;
}

// ── 3-dot menu ────────────────────────────────────────────────────────────────

interface ItemMenuProps {
  currentStep: number;
  steps: number[];
  onMove: (step: number) => void;
  onRemove: () => void;
  onClose: () => void;
}

function ItemMenu({ currentStep, steps, onMove, onRemove, onClose }: ItemMenuProps) {
  const [showMoveList, setShowMoveList] = useState(false);
  const otherSteps = steps.filter(s => s !== currentStep);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid="reveal-item-menu"
      className="absolute right-0 top-5 z-50 bg-zinc-900 border border-white/10 rounded-lg shadow-xl shadow-black/40 py-1 min-w-[160px]"
      onClick={e => e.stopPropagation()}
    >
      <button
        className="w-full px-3 py-1.5 text-left text-xs text-zinc-200 hover:bg-zinc-800 flex items-center justify-between transition-colors"
        onClick={() => setShowMoveList(v => !v)}
      >
        Move to step…
        <ChevronDown size={11} className={`text-zinc-500 transition-transform ${showMoveList ? "rotate-180" : ""}`} />
      </button>

      {showMoveList && (
        <div className="border-t border-white/5 mb-1">
          {otherSteps.length === 0 ? (
            <p className="px-4 py-1.5 text-xs text-zinc-500">No other steps</p>
          ) : (
            otherSteps.map(s => (
              <button
                key={s}
                data-testid={`move-to-step-${s}`}
                className="w-full px-4 py-1.5 text-left text-xs text-zinc-300 hover:bg-zinc-800 transition-colors"
                onClick={() => { onMove(s); onClose(); }}
              >
                Step {s}
              </button>
            ))
          )}
        </div>
      )}

      <div className="h-px bg-white/10 my-1" />

      <button
        data-testid="reveal-item-remove"
        className="w-full px-3 py-1.5 text-left text-xs font-bold text-red-400 bg-red-950/50 hover:bg-red-900/70 transition-colors flex items-center gap-2"
        onClick={() => { onRemove(); onClose(); }}
      >
        <Trash2 size={12} className="shrink-0" />
        Remove
      </button>
    </div>
  );
}

// ── Item row ──────────────────────────────────────────────────────────────────

interface RowProps {
  item: RevealItem;
  allSteps: number[];
  isHighlighted: boolean;
  isDragOver: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent) => void;
  onMoveToStep: (step: number) => void;
  onRemove: () => void;
}

function RevealRow({
  item, allSteps, isHighlighted, isDragOver,
  onMouseEnter, onMouseLeave,
  onDragStart, onDragEnter, onDragEnd, onDrop,
  onMoveToStep, onRemove,
}: RowProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      data-testid={`reveal-row-${item.rfId}`}
      draggable
      className={`
        flex items-center gap-1.5 px-2 py-0.5 cursor-grab active:cursor-grabbing
        transition-colors relative group select-none
        ${isHighlighted ? "bg-yellow-400/15 ring-1 ring-inset ring-yellow-400/50" : "hover:bg-zinc-800/60"}
        ${isDragOver ? "border-t border-indigo-400" : "border-t border-transparent"}
      `}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      onDragOver={e => e.preventDefault()}
    >
      <GripVertical size={11} className="text-zinc-600 shrink-0" />

      <div className="flex-1 min-w-0 flex items-baseline gap-1.5">
        <span className="text-xs text-zinc-200 truncate">{item.label}</span>
        <span className="text-[10px] text-zinc-500 shrink-0">{item.subLabel}</span>
      </div>

      <div className="relative shrink-0">
        <button
          data-testid={`reveal-row-menu-${item.rfId}`}
          className="p-0.5 text-zinc-400 hover:text-zinc-100 transition-colors"
          onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
        >
          <MoreVertical size={11} />
        </button>

        {menuOpen && (
          <ItemMenu
            currentStep={item.step}
            steps={allSteps}
            onMove={onMoveToStep}
            onRemove={onRemove}
            onClose={() => setMenuOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

// ── Main bar ──────────────────────────────────────────────────────────────────

export default function RevealEditBar({ onClose, onDragHandleMouseDown }: RevealEditBarProps) {
  const {
    rfNodes, rfShapes,
    revealSequence, revealHighlightId,
    setRevealHighlightId, removeFromRevealSequence,
    moveToRevealStep, reorderWithinStep,
  } = useCanvasStore();

  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [dragId, setDragId]         = useState<string | null>(null);
  const [dragOver, setDragOver]     = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setActiveStep(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const { stepGroups, allSteps } = useMemo((): { stepGroups: StepGroup[]; allSteps: number[] } => {
    const byStep = new Map<number, RevealItem[]>();

    revealSequence.forEach(rfId => {
      const node  = rfNodes.find(n => n.id === rfId);
      const shape = rfShapes.find(n => n.id === rfId);
      if (node) {
        const d = node.data as KFNodeData;
        if (!d.revealStep) return;
        if (!byStep.has(d.revealStep)) byStep.set(d.revealStep, []);
        byStep.get(d.revealStep)!.push({ rfId, label: d.label, subLabel: d.type, step: d.revealStep });
      } else if (shape) {
        const d = shape.data as unknown as CanvasShape;
        if (!d.revealStep) return;
        if (!byStep.has(d.revealStep)) byStep.set(d.revealStep, []);
        byStep.get(d.revealStep)!.push({ rfId, label: d.kind, subLabel: "shape", step: d.revealStep });
      }
    });

    // Also catch elements with step > 0 not yet in sequence
    [...rfNodes, ...rfShapes].forEach(n => {
      const step = (n.data as KFNodeData).revealStep as number;
      if (!step) return;
      if (revealSequence.includes(n.id)) return;
      if (!byStep.has(step)) byStep.set(step, []);
      const d = n.data as KFNodeData;
      const label    = d.label    ?? (n.data as unknown as CanvasShape).kind ?? n.id;
      const subLabel = d.type     ?? "shape";
      byStep.get(step)!.push({ rfId: n.id, label, subLabel, step });
    });

    const sorted = Array.from(byStep.entries())
      .sort(([a], [b]) => a - b)
      .map(([step, items]) => ({ step, items }));

    return { stepGroups: sorted, allSteps: sorted.map(g => g.step) };
  }, [rfNodes, rfShapes, revealSequence]);

  const activeItems = useMemo(
    () => stepGroups.find(g => g.step === activeStep)?.items ?? [],
    [stepGroups, activeStep],
  );

  const handleDragStart = useCallback((e: React.DragEvent, rfId: string) => {
    setDragId(rfId);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string, step: number) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    reorderWithinStep(dragId, targetId, step);
    setDragId(null);
    setDragOver(null);
  }, [dragId, reorderWithinStep]);

  return (
    <div
      ref={barRef}
      data-testid="reveal-edit-bar"
      className="relative"
      style={{ maxWidth: 520 }}
    >
      {/* Step tabs row */}
      <div className={`flex items-center gap-1 ${THEME.toolbar.container} rounded-lg px-1.5 py-1 shadow-lg shadow-black/40`}>
        {onDragHandleMouseDown && (
          <div
            onMouseDown={onDragHandleMouseDown}
            className="cursor-grab active:cursor-grabbing text-zinc-400 hover:text-zinc-100 transition-colors pr-1"
            title="Drag to move"
          >
            <GripVertical size={12} />
          </div>
        )}
        {stepGroups.length === 0 ? (
          <span className="text-[11px] text-zinc-500 px-1">No steps — set Reveal Step via Properties</span>
        ) : (
          stepGroups.map(({ step, items }) => (
            <button
              key={step}
              data-testid={`reveal-step-tab-${step}`}
              onClick={() => setActiveStep(activeStep === step ? null : step)}
              className={`
                flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] transition-colors
                ${activeStep === step ? THEME.toolbar.buttonActive : THEME.toolbar.buttonInactive}
              `}
            >
              Step {step}
              <span className="text-[9px] bg-zinc-700/60 rounded px-1 tabular-nums">{items.length}</span>
            </button>
          ))
        )}

        <div className={`w-px h-4 ${THEME.toolbar.divider} mx-auto`} />

        <button
          data-testid="reveal-edit-bar-close"
          onClick={onClose}
          className={`p-1 rounded-md ${THEME.toolbar.buttonInactive}`}
          title="Close sequence editor"
        >
          <X size={12} />
        </button>
      </div>

      {/* Expanded items for active step — floats below, doesn't affect toolbar layout */}
      {activeStep !== null && (
        <div
          data-testid={`reveal-step-items-${activeStep}`}
          className={`absolute top-full left-0 mt-1 z-40 ${THEME.toolbar.container} rounded-lg shadow-lg shadow-black/40`}
          style={{ minWidth: "100%" }}
        >
          {activeItems.length === 0 ? (
            <p className="text-[11px] text-zinc-500 px-3 py-2">No items in this step.</p>
          ) : (
            <div className="py-1">
              {activeItems.map(item => (
                <RevealRow
                  key={item.rfId}
                  item={item}
                  allSteps={allSteps}
                  isHighlighted={revealHighlightId === item.rfId}
                  isDragOver={dragOver === item.rfId}
                  onMouseEnter={() => setRevealHighlightId(item.rfId)}
                  onMouseLeave={() => setRevealHighlightId(null)}
                  onDragStart={e => handleDragStart(e, item.rfId)}
                  onDragEnter={e => { e.preventDefault(); setDragOver(item.rfId); }}
                  onDragEnd={() => { setDragId(null); setDragOver(null); }}
                  onDrop={e => handleDrop(e, item.rfId, activeStep)}
                  onMoveToStep={newStep => { moveToRevealStep(item.rfId, newStep); setActiveStep(newStep); }}
                  onRemove={() => removeFromRevealSequence(item.rfId)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
